// Remembering which ad a visitor arrived from.
//
// An ad platform's UTM link only proves somebody clicked. It says nothing
// about the lead unless the campaign is still attached when the form is
// submitted — and the ad lands on /ai/demos while the form lives on /demo, so
// the parameters have to survive at least one navigation to get there.
//
// They are kept in sessionStorage: long enough to follow the visitor through
// the site, gone when they close the tab, and never shared across tabs the way
// a cookie would be. Nothing here is a tracking identifier for the person —
// it is the campaign, the ad, and the page they landed on.

const KEY = "ownpbx_attribution";

const FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
// Whatever ad platform is in play stamps its own click id; any one of them
// answers the same question, so the first one present is kept.
const CLICK_IDS = ["gclid", "fbclid", "msclkid", "ttclid", "li_fat_id"];

const read = () => {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || "null");
  } catch {
    return null; // private mode, blocked storage, or someone edited it by hand
  }
};

const write = (data) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* storage unavailable — attribution is best-effort, the form still works */
  }
};

/**
 * Call on every navigation. A URL carrying campaign parameters is a fresh ad
 * click and replaces what was stored; anything else leaves the original click
 * alone, so browsing from the landing page to the form doesn't erase it.
 */
export function captureAttribution(search = window.location.search) {
  const q = new URLSearchParams(search);
  const hit = {};
  for (const f of FIELDS) {
    const v = q.get(f);
    if (v) hit[f] = v.slice(0, 200);
  }
  const clickId = CLICK_IDS.map((k) => q.get(k)).find(Boolean);
  if (clickId) hit.click_id = clickId.slice(0, 200);

  if (!Object.keys(hit).length) return read();

  const data = {
    ...hit,
    landing_page: window.location.href.slice(0, 500),
    // Where they were before us — empty when they typed the address or came
    // from an app that strips it, which is normal and not an error.
    referrer: (document.referrer || "").slice(0, 500),
    captured_at: new Date().toISOString(),
  };
  write(data);
  return data;
}

/** The stored campaign, flattened for posting with a form. */
export function getAttribution() {
  const d = read() || {};
  const out = {};
  for (const k of [...FIELDS, "click_id", "landing_page", "referrer"]) {
    if (d[k]) out[k] = d[k];
  }
  return out;
}
